import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import app from "./firebaseApp.js";
import { getAuth } from "firebase/auth";
import {
  syncAdminDevTools,
  shouldShowAdminDarkLoading,
  hasStoredAdminDevPreferences,
} from "./adminDevTools.js";
import { canUseAdminDevTools } from "./adminUsers.js";

export const AuthContext = React.createContext({
  currentUser: null,
  isEmailVerified: false,
  refreshUser: async () => {},
});

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export const AuthProvider = ({ children }) => {
  const auth = getAuth(app);
  const [currentUser, setCurrentUser] = useState(null);
  const [pending, setPending] = useState(true);
  // Bumped after user.reload() so derived values (emailVerified) re-render
  // even though Firebase mutates the same User instance.
  const [authTick, setAuthTick] = useState(0);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setPending(false);
    });
    return () => unsubscribe();
  }, [auth]);

  useLayoutEffect(() => {
    syncAdminDevTools(currentUser?.email ?? auth.currentUser?.email, { authPending: pending });
  }, [currentUser?.email, auth, pending]);

  const refreshUser = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return null;
    await user.reload();
    setCurrentUser(auth.currentUser);
    setAuthTick((tick) => tick + 1);
    return auth.currentUser;
  }, [auth]);

  const value = useMemo(
    () => ({
      currentUser,
      isEmailVerified: Boolean(currentUser?.emailVerified),
      refreshUser,
      authTick,
    }),
    [currentUser, refreshUser, authTick],
  );

  if (pending) {
    const loadingEmail = currentUser?.email ?? auth.currentUser?.email;

    syncAdminDevTools(loadingEmail, { authPending: true });

    const isDarkLoading = shouldShowAdminDarkLoading(loadingEmail);
    const showAdminLoadingSidebar =
      canUseAdminDevTools(loadingEmail) ||
      (!loadingEmail && hasStoredAdminDevPreferences());

    return (
      <div
        className={classNames(
          'auth-loading-screen flex h-full min-h-screen flex-row',
          isDarkLoading ? 'auth-loading-screen--dark' : 'bg-gray-50',
        )}
      >
        {showAdminLoadingSidebar && (
          <div className="auth-loading-sidebar app-sidebar ex-sidebar hidden h-full shrink-0 lg:block" />
        )}
        <div
          className={classNames(
            'auth-loading-main flex h-screen flex-1 flex-col items-center justify-center gap-4',
            isDarkLoading ? 'auth-loading-screen--dark' : 'bg-gray-50',
          )}
        >
          <img
            className="h-8 w-auto opacity-90"
            src={process.env.PUBLIC_URL + (isDarkLoading ? '/logo_extracta_labs_white.png' : '/logo_extracta_labs_main.png')}
            alt="Extracta LABS"
          />
          <div role="status" className="flex items-center gap-2 text-sm text-gray-500">
            <svg aria-hidden="true" className="h-5 w-5 animate-spin text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
